import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  CopyToClipboard,
  Button,
  LiveRegion,
  ExpandableSection,
  Popover,
  StatusIndicator,
  MultiselectProps,
  NonCancelableCustomEvent
} from '@amzn/awsui-components-core';

import { EmptyStateMessage } from '../../fintech-ops/EmptyStateMessage';
import { ChartGenerator } from './ChartGenerator';
import { CustomPromptInput } from './CustomPromptInput';
import {
  handleSelectionChange,
  handleBlur,
  handleOptimizePrompt,
  handleGenerateCustomSummary,
  handleGenerateCustomCharts,
  resetToOriginalSummary,
  validatePromptContent,
  getPlaceholderText
} from './utils/actionHandlers';
import LoadingBar from '@amzn/awsui-chat-components/loading-bar';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { fetchPageSummary } from 'src/api/app-sync-events-service';
import { v4 as uuidv4 } from 'uuid';
import { FeedbackButtons } from './FeedbackButtons';
import { VALID_PROMPT_KEYWORDS, RESTRICTED_PROMPT_KEYWORDS, AI_OPTIMIZATION_PROMPT_TEMPLATE, ENHANCE_SUMMARY_PROMPT_TEMPLATE } from './constants';
import { useEventsApiContext } from 'src/components/context/EventsApiContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { ApiErrorHandler, ApiResponseError, REQUEST_TIMEOUT, createTimeoutController, handleTimeoutError } from 'src/utilities/ApiErrorHandler';
import { processFiltersForAISummary } from 'src/utilities/CommonUtilities';
import styles from './ai-summary-components.module.scss';
import { logger } from 'src/logger';
import { barLineChartTransformer, pieChartTransformer, parseChartData } from './utils/customChartUtils';
import { BarLineChartProps, PieChartData, ChartData } from './types/interfaces';

type ChartProps = BarLineChartProps | PieChartData;

interface EnhancedAISummaryBoxProps {
  pageType: string;
  filters: string;
  title?: string;
  /** Optional caption rendered directly under the title (e.g. an AI disclaimer). */
  titleCaption?: string;
  statusKey?: string;
  statusValue?: string;
  aggregatedData: string;
  originalOptions?: any;
  /** When false, hides the custom-analysis footer (Get Custom Analysis controls) and shows summary only. Defaults to true. */
  showCustomAnalysis?: boolean;
  // Additional props for custom functionality
  processedTicketData?: any;
  processedUserData?: any;
  businessFilters?: any;
}

interface OptimizePromptResponse {
  optimizePrompt: {
    optimizedPrompt: string;
    suggestions: string[];
  };
}

interface CustomSummaryResponse {
  generateCustomSummary: {
    summary: string;
    keyInsights: string[];
    recommendations: string[];
    timestamp: string;
  };
}

export const EnhancedAISummaryBox: React.FC<EnhancedAISummaryBoxProps> = ({
  pageType,
  filters,
  title = 'Summary',
  titleCaption,
  statusKey,
  statusValue = 'finished',
  aggregatedData,
  originalOptions,
  showCustomAnalysis = true,
  processedTicketData,
  processedUserData,
  businessFilters
}) => {
  const [summary, setSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [summaryId, setSummaryId] = useState<string>('');

  // Custom prompt states
  const [showCustomPrompt, setShowCustomPrompt] = useState<boolean>(false);
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [optimizedPrompt, setOptimizedPrompt] = useState<string>('');
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
  const [isGeneratingCustom, setIsGeneratingCustom] = useState<boolean>(false);
  const [isGeneratingCustomChart, setIsGeneratingCustomChart] = useState<boolean>(false);
  const [customError, setCustomError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [originalSummary, setOriginalSummary] = useState<string>('');
  const [originalSummaryId, setOriginalSummaryId] = useState<string>('');
  const [summaryMode, setSummaryMode] = useState<'new' | 'enhance'>('new');
  const [requestStartTime, setRequestStartTime] = useState<number | null>(null);
  const [customCharts, setCustomCharts] = useState<ChartProps[]>([]);
  const [showCustomCharts, setShowCustomCharts] = useState<boolean>(false);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [completedActions, setCompletedActions] = useState<string[]>([]);

  // Reset actions and hide charts if prompt contains restricted content
  useEffect(() => {
    setSelectedActions([]);
    setCompletedActions([]);

    // Hide charts if prompt contains restricted content
    if (validatePromptContent(userPrompt).hasRestrictedContent) {
      setShowCustomCharts(false);
    }
  }, [userPrompt]);

  // Reset actions when optimized prompt changes
  useEffect(() => {
    setSelectedActions([]);
    setCompletedActions([]);
  }, [optimizedPrompt]);

  // Action options for multiselect
  const actionOptions = [
    {
      label: 'Select All',
      options: [
        { value: 'generateSummary', label: 'Generate Custom Summary' },
        { value: 'generateCharts', label: 'Generate Custom Charts' }
      ]
    }
  ];

  // Context hooks
  const eventsApiContext = useEventsApiContext();
  const authContext = useAuth();

  const handleOptimizePromptLocal = async () => {
    await handleOptimizePrompt({
      userPrompt,
      pageType,
      filters,
      aggregatedData,
      eventsApiContext,
      authContext,
      setIsOptimizing,
      setCustomError,
      setOptimizedPrompt,
      setSuggestions,
      setRequestStartTime,
      validatePromptContent
    });
  };

  const handleGenerateCustomSummaryLocal = async () => {
    await handleGenerateCustomSummary({
      userPrompt,
      optimizedPrompt,
      pageType,
      filters,
      aggregatedData,
      eventsApiContext,
      authContext,
      summaryMode,
      originalSummary,
      setIsGeneratingCustom,
      setCustomError,
      setRequestStartTime,
      setSummary,
      setSummaryId
    });
  };

  const handleGenerateCustomChartsLocal = async () => {
    await handleGenerateCustomCharts({
      userPrompt,
      pageType,
      filters,
      aggregatedData,
      eventsApiContext,
      authContext,
      setIsGeneratingCustomChart,
      setCustomError,
      setRequestStartTime,
      setCustomCharts,
      setShowCustomCharts
    });
  };

  const resetToOriginalSummaryLocal = () => {
    resetToOriginalSummary({
      originalSummary,
      originalSummaryId,
      setSummary,
      setSummaryId,
      setUserPrompt,
      setOptimizedPrompt,
      setSuggestions,
      setCustomError,
      setShowCustomPrompt,
      setShowCustomCharts,
      setCompletedActions
    });
  };

  const handleSelectionChangeLocal = (event: NonCancelableCustomEvent<MultiselectProps.MultiselectChangeDetail>) => {
    handleSelectionChange(event, {
      selectedActions,
      setSelectedActions,
      completedActions,
      setCompletedActions,
      setOptimizedPrompt,
      setSuggestions
    });
  };

  const handleBlurLocal = async () => {
    await handleBlur({
      selectedActions,
      completedActions,
      setCompletedActions,
      setShowCustomCharts,
      setCustomError,
      userPrompt,
      validatePromptContent,
      handleGenerateCustomSummary: handleGenerateCustomSummaryLocal,
      handleGenerateCustomCharts: handleGenerateCustomChartsLocal
    } as any);
  };

  useEffect(() => {
    setShouldFetch(!statusKey || !statusValue || statusKey === statusValue);
  }, [statusKey, statusValue]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!shouldFetch || !pageType) return;

      const currentFilters = processFiltersForAISummary(filters, originalOptions);
      const cacheKey = `${pageType}-${filters}`;

      // Cancel any existing request for this pageType before proceeding
      const wasCanceled = eventsApiContext.cancelPageRequest(pageType);
      if (wasCanceled) {
        logger.info(`Canceled previous request for pageType: ${pageType}`);
      }

      const cachedEntry = eventsApiContext.getCacheEntry(cacheKey);
      if (cachedEntry && 'summary' in cachedEntry) {
        setSummary(cachedEntry.summary);
        setOriginalSummary(cachedEntry.summary);
        setOriginalSummaryId(cachedEntry.summaryId);
        setSummaryId(cachedEntry.summaryId);
        setError(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        setSummary('');

        const sessionId = uuidv4();
        const { message, summaryId: receivedSummaryId } = await fetchPageSummary(
          sessionId,
          pageType,
          currentFilters,
          aggregatedData,
          eventsApiContext,
          authContext.Alias || 'unknown'
        );
        setSummary(message);
        setOriginalSummary(message);
        setOriginalSummaryId(receivedSummaryId);
        setSummaryId(receivedSummaryId);

        eventsApiContext.setCacheEntry(cacheKey, {
          summary: message,
          summaryId: receivedSummaryId
        });
      } catch (error) {
        console.error('Error fetching events summary:', error);
        const errorMsg = 'Unable to load summary';
        setSummary(errorMsg);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (shouldFetch) {
      fetchSummary();
    }
  }, [shouldFetch, pageType, filters, aggregatedData]);

  return (
    <Box padding="m">
      <Container
        header={
          <Header variant="h2" description={titleCaption}>
            {title}
          </Header>
        }
        footer={
          /* Custom analysis controls moved to footer for easy access without scrolling */
          showCustomAnalysis &&
          !loading &&
          !error &&
          summary && (
            <SpaceBetween direction="vertical" size="s">
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <Button variant="primary" iconName="gen-ai" onClick={() => setShowCustomPrompt(!showCustomPrompt)} disabled={isGeneratingCustom}>
                  {showCustomPrompt ? 'Hide Custom Analysis' : 'Get Custom Analysis'}
                </Button>
                {((showCustomPrompt && summary !== originalSummary) || showCustomCharts) && (
                  <Button variant="normal" iconName="refresh" onClick={resetToOriginalSummaryLocal} disabled={isGeneratingCustom}>
                    Reset to Original
                  </Button>
                )}
                {!showCustomPrompt && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    Ask specific questions about your data for deeper insights
                  </Box>
                )}
              </SpaceBetween>

              {showCustomPrompt && (
                <ExpandableSection
                  headerText={
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      Custom Analysis Options
                      <Popover
                        size="small"
                        position="top"
                        triggerType="custom"
                        dismissButton={false}
                        content={
                          <Box padding="s">
                            <SpaceBetween direction="vertical" size="xs">
                              <Box variant="strong">Custom Prompt Usage</Box>
                              <Box>This feature is designed for analyzing:</Box>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                <li>Metrics and Resolution patterns</li>
                                <li>Business Reporting Data Insights</li>
                                <li>Performance trends and analytics</li>
                              </ul>
                            </SpaceBetween>
                          </Box>
                        }
                      >
                        <StatusIndicator type="info" />
                      </Popover>
                    </SpaceBetween>
                  }
                  defaultExpanded
                >
                  <SpaceBetween direction="vertical" size="xs">
                    <CustomPromptInput
                      userPrompt={userPrompt}
                      optimizedPrompt={optimizedPrompt}
                      suggestions={suggestions}
                      customError={customError}
                      isOptimizing={isOptimizing}
                      isGeneratingCustom={isGeneratingCustom}
                      isGeneratingCustomChart={isGeneratingCustomChart}
                      selectedActions={selectedActions}
                      onPromptChange={(value: string) => setUserPrompt(value)}
                      onOptimizePrompt={handleOptimizePromptLocal}
                      onSelectionChange={handleSelectionChangeLocal}
                      onBlur={handleBlurLocal}
                      onDismissError={() => setCustomError(null)}
                      placeholder={getPlaceholderText(pageType)}
                    />

                    {(showCustomCharts || isGeneratingCustomChart) && !validatePromptContent(userPrompt).hasRestrictedContent && (
                      <ExpandableSection headerText="Prompt-Generated Charts" variant="default" defaultExpanded>
                        <SpaceBetween direction="vertical" size="l">
                          {isGeneratingCustomChart ? (
                            <Box padding="l">
                              <LiveRegion>
                                <SpaceBetween direction="vertical" size="s">
                                  <Box color="text-body-secondary">Generating custom charts...</Box>
                                  <div className="ai-summary-loading-bar">
                                    <LoadingBar variant="gen-ai" />
                                  </div>
                                </SpaceBetween>
                              </LiveRegion>
                            </Box>
                          ) : (
                            <ChartGenerator charts={customCharts} isLoading={false} error={customError} showCharts={showCustomCharts} />
                          )}
                        </SpaceBetween>
                      </ExpandableSection>
                    )}
                  </SpaceBetween>
                </ExpandableSection>
              )}
            </SpaceBetween>
          )
        }
      >
        <SpaceBetween direction="vertical" size="xs">
          {/* Main Summary Content - Clean and focused */}
          <div>
            {loading || isGeneratingCustom ? (
              <Box padding="l">
                <LiveRegion>
                  <SpaceBetween direction="vertical" size="s">
                    <Box color="text-body-secondary">{isGeneratingCustom ? 'Generating custom analysis...' : 'Generating the summary...'}</Box>
                    <div className="ai-summary-loading-bar">
                      <LoadingBar variant="gen-ai" />
                    </div>
                  </SpaceBetween>
                </LiveRegion>
              </Box>
            ) : error ? (
              <Box textAlign="center" padding="l" color="text-status-error">
                <Box variant="p">Unable to load summary. Please try again later.</Box>
              </Box>
            ) : (
              <>
                <Box padding={{ horizontal: 's', vertical: 'xs' }}>
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(summary) }} />
                </Box>
                {/* Compact feedback and copy buttons in one line */}
                {summary && summaryId && (
                  <Box padding={{ horizontal: 's', top: 'xxs' }}>
                    <div className={styles.feedbackCopyLayout}>
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <Box fontSize="body-s" color="text-body-secondary">
                          Was this helpful?
                        </Box>
                        <FeedbackButtons summaryId={summaryId} userId={authContext.Alias || 'unknown'} />
                      </SpaceBetween>
                      <CopyToClipboard
                        copyButtonAriaLabel="Copy Summary"
                        copyErrorText="Failed to copy"
                        copySuccessText="Summary copied"
                        textToCopy={summary.replace(/<[^>]*>/g, '')} // Strip HTML for copy
                        variant="icon"
                      />
                    </div>
                  </Box>
                )}
              </>
            )}
          </div>
        </SpaceBetween>
      </Container>
    </Box>
  );
};
